import React, { useState, useMemo } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function CourseSessionsTabModern({
  sessions,
  authHeaders,
  toast,
  fetchSessions,
  formatDateISOToGerman,
  isSuperAdmin,
  isAdmin,
}) {
  const [seriesStart, setSeriesStart] = useState("");
  const [seriesEnd, setSeriesEnd] = useState("");
  const [noEnd, setNoEnd] = useState(true);
  const [seriesError, setSeriesError] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all"); // all, active, inactive

  const canManage = isSuperAdmin || isAdmin;

  // Sortierte Sessions (neueste zuerst)
  const sortedSessions = useMemo(() => {
    let list = [...(sessions || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
    if (filterStatus === "active") {
      list = list.filter((s) => s.active);
    } else if (filterStatus === "inactive") {
      list = list.filter((s) => !s.active);
    }
    return list;
  }, [sessions, filterStatus]);

  // Statistiken
  const stats = useMemo(() => {
    const all = sessions || [];
    const active = all.filter((s) => s.active).length;
    const inactive = all.filter((s) => !s.active).length;
    const upcoming = all.filter((s) => new Date(s.date) >= new Date() && s.active).length;
    return { total: all.length, active, inactive, upcoming };
  }, [sessions]);

  // Kurstermine generieren
  const handleGenerateWeeklySeries = async () => {
    setSeriesError("");
    if (!seriesStart) {
      setSeriesError("Bitte Startdatum angeben.");
      return;
    }
    if (!noEnd && !seriesEnd) {
      setSeriesError("Bitte Enddatum angeben oder 'ohne festes Enddatum' wählen.");
      return;
    }

    setIsGenerating(true);
    try {
      await axios.post(
        `${API_BASE}/api/sessions/generate-series`,
        {
          start_date: seriesStart,
          end_date: noEnd ? null : seriesEnd,
        },
        authHeaders()
      );
      toast({ title: "Wöchentliche Kurstermine wurden erfolgreich erzeugt." });
      fetchSessions();
      setShowGenerator(false);
      setSeriesStart("");
      setSeriesEnd("");
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Fehler beim Erzeugen der Kurstermine";
      setSeriesError(msg);
    } finally {
      setIsGenerating(false);
    }
  };

  // Alle löschen
  const handleDeleteAll = async () => {
    try {
      await axios.delete(`${API_BASE}/api/sessions`, authHeaders());
      toast({ title: "Alle Kurstermine wurden gelöscht." });
      fetchSessions();
      setDeleteConfirm(false);
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Löschen der Kurstermine", variant: "destructive" });
    }
  };

  // Termin aktivieren/deaktivieren
  const toggleSession = async (session) => {
    try {
      await axios.post(
        `${API_BASE}/api/sessions/${session.id}/active`,
        { active: !session.active },
        authHeaders()
      );
      toast({
        title: session.active
          ? "Termin deaktiviert (kein Kurs an diesem Tag)"
          : "Termin wieder aktiviert",
      });
      fetchSessions();
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Aktualisieren des Kurstermins", variant: "destructive" });
    }
  };

  // Prüfen ob Termin heute ist
  const isToday = (dateStr) => {
    const today = new Date().toISOString().split("T")[0];
    return dateStr === today;
  };

  // Prüfen ob Termin in der Vergangenheit
  const isPast = (dateStr) => {
    return new Date(dateStr) < new Date(new Date().toISOString().split("T")[0]);
  };

  return (
    <div className="space-y-4">
      {/* Header mit Statistiken */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-white p-4 shadow-sm">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Kurstermine</h2>
          <p className="text-sm text-slate-500">Wöchentliche Schwimmtermine verwalten</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-violet-600">{stats.upcoming}</div>
            <div className="text-xs text-slate-500">Kommende</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-600">{stats.active}</div>
            <div className="text-xs text-slate-500">Aktiv</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-400">{stats.inactive}</div>
            <div className="text-xs text-slate-500">Deaktiviert</div>
          </div>
        </div>
      </div>

      {/* Aktionen */}
      {canManage && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowGenerator(!showGenerator)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
              showGenerator
                ? "bg-violet-600 text-white"
                : "bg-violet-100 text-violet-700 hover:bg-violet-200"
            }`}
          >
            {showGenerator ? "Generator schließen" : "+ Termine generieren"}
          </button>
          {stats.total > 0 && (
            <button
              onClick={() => setDeleteConfirm(true)}
              className="rounded-full bg-rose-50 px-4 py-2 text-sm font-medium text-rose-600 hover:bg-rose-100"
            >
              Alle löschen
            </button>
          )}
        </div>
      )}

      {/* Lösch-Bestätigung */}
      {deleteConfirm && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-100">
              <svg className="h-5 w-5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-rose-900">Alle Kurstermine löschen?</h4>
              <p className="mt-1 text-xs text-rose-700">
                Möchten Sie wirklich <strong>alle {stats.total} Kurstermine</strong> unwiderruflich löschen?
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => setDeleteConfirm(false)}
                  className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleDeleteAll}
                  className="rounded-full bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-500"
                >
                  Ja, alle löschen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generator */}
      {showGenerator && canManage && (
        <div className="rounded-xl bg-gradient-to-br from-violet-50 to-white p-4 shadow-sm">
          <h3 className="mb-3 text-lg font-semibold text-violet-900">Wöchentliche Kurstermine erzeugen</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs text-slate-500">Startdatum (Samstag)</label>
              <input
                type="date"
                value={seriesStart}
                onChange={(e) => setSeriesStart(e.target.value)}
                className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Enddatum</label>
              <input
                type="date"
                value={seriesEnd}
                onChange={(e) => setSeriesEnd(e.target.value)}
                disabled={noEnd}
                className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50"
              />
              <label className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={noEnd}
                  onChange={(e) => setNoEnd(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                />
                Ohne Enddatum (1 Jahr)
              </label>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleGenerateWeeklySeries}
                disabled={isGenerating}
                className="w-full rounded-full bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:opacity-50"
              >
                {isGenerating ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Generiere...
                  </span>
                ) : (
                  "Termine erzeugen"
                )}
              </button>
            </div>
          </div>
          {seriesError && (
            <p className="mt-2 text-xs text-rose-600">{seriesError}</p>
          )}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">Filter:</span>
        {[
          { value: "all", label: `Alle (${stats.total})` },
          { value: "active", label: `Aktiv (${stats.active})` },
          { value: "inactive", label: `Deaktiviert (${stats.inactive})` },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilterStatus(f.value)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${
              filterStatus === f.value
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Termine-Liste */}
      <div className="rounded-xl bg-white shadow-sm overflow-hidden">
        {sortedSessions.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mb-2 text-4xl">📅</div>
            <p className="text-sm text-slate-500">
              {filterStatus === "all"
                ? "Noch keine Kurstermine angelegt."
                : filterStatus === "active"
                ? "Keine aktiven Termine."
                : "Keine deaktivierten Termine."}
            </p>
            {canManage && filterStatus === "all" && (
              <button
                onClick={() => setShowGenerator(true)}
                className="mt-3 rounded-full bg-violet-100 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-200"
              >
                Jetzt Termine generieren
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {sortedSessions.map((session) => {
              const today = isToday(session.date);
              const past = isPast(session.date);
              
              return (
                <div
                  key={session.id}
                  className={`flex items-center justify-between p-3 transition-all ${
                    today ? "bg-amber-50" : past ? "bg-slate-50" : ""
                  } ${!session.active ? "opacity-60" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Datum */}
                    <div className={`flex h-12 w-12 flex-col items-center justify-center rounded-lg ${
                      today ? "bg-amber-100" : session.active ? "bg-violet-100" : "bg-slate-200"
                    }`}>
                      <span className={`text-lg font-bold ${
                        today ? "text-amber-700" : session.active ? "text-violet-700" : "text-slate-500"
                      }`}>
                        {new Date(session.date).getDate()}
                      </span>
                      <span className={`text-[10px] font-medium ${
                        today ? "text-amber-600" : session.active ? "text-violet-600" : "text-slate-400"
                      }`}>
                        {new Date(session.date).toLocaleDateString("de-DE", { month: "short" })}
                      </span>
                    </div>
                    
                    {/* Info */}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">
                          {session.weekday}, {formatDateISOToGerman(session.date)}
                        </span>
                        {today && (
                          <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                            HEUTE
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          session.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
                        }`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${
                            session.active ? "bg-emerald-500" : "bg-slate-400"
                          }`} />
                          {session.active ? "Aktiv" : "Deaktiviert"}
                        </span>
                        {past && session.active && (
                          <span className="text-[10px] text-slate-400">Vergangen</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Aktion */}
                  {canManage && (
                    <button
                      onClick={() => toggleSession(session)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                        session.active
                          ? "bg-slate-100 text-slate-600 hover:bg-rose-100 hover:text-rose-600"
                          : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                      }`}
                    >
                      {session.active ? "Deaktivieren" : "Aktivieren"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
